import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../lib/cart";
import { FaTiktok } from "react-icons/fa";
import { Gift, ArrowRight, Sparkles, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const CODE = "TIKTOK10";

export default function TikTokLandingPage() {
  const [products, setProducts] = useState([]);
  const { setDiscount } = useCart();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Auto-apply the discount as soon as a TikTok visitor lands here
    setDiscount(CODE);
    api.get("/products").then(r => setProducts(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const copyCode = () => {
    navigator.clipboard.writeText(CODE);
    setCopied(true);
    toast.success("Code copied!");
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div data-testid="tiktok-landing" className="bg-brand-ink text-brand-bg">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img
            src="https://customer-assets.emergentagent.com/job_ankara-vibes/artifacts/6teneoxn_IMG-20260606-WA0018.jpg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/60 via-brand-ink/80 to-brand-ink" />
        </div>
        <div className="relative z-10 max-w-[800px] mx-auto px-6 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 text-brand-gold mb-6 animate-fade-up">
            <FaTiktok size={20} />
            <span className="overline text-brand-gold">From TikTok with love</span>
          </div>
          <h1 className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95] animate-fade-up" style={{animationDelay:"100ms"}}>
            Welcome,<br />
            <span className="italic text-brand-gold">beautiful.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-brand-bg/85 max-w-md mx-auto animate-fade-up" style={{animationDelay:"200ms"}}>
            You found us through TikTok — here's <span className="text-brand-gold font-medium">10% off</span> your first cindymaks piece. Just for you.
          </p>

          {/* Code reveal card */}
          <div className="mt-10 max-w-sm mx-auto animate-fade-up" style={{animationDelay:"300ms"}}>
            <div className="bg-brand-gold/10 border border-brand-gold/40 backdrop-blur-sm p-6">
              <div className="overline text-brand-gold mb-3 flex items-center justify-center gap-2">
                <Gift size={14} /> Your code
              </div>
              <button
                onClick={copyCode}
                data-testid="copy-discount-code"
                className="w-full flex items-center justify-center gap-3 bg-brand-gold text-brand-ink py-4 font-serif text-2xl tracking-[0.3em] font-medium hover:bg-brand-bg transition-colors"
              >
                {CODE}
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </button>
              <p className="text-xs text-brand-bg/70 mt-3">
                Applied automatically at checkout · One-time per customer
              </p>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center animate-fade-up" style={{animationDelay:"400ms"}}>
            <Link
              to="/shop"
              data-testid="tiktok-shop-btn"
              className="btn-primary inline-flex items-center gap-2"
              style={{ background: "#D4AF6A", color: "#0A0807" }}
            >
              Shop the collection <ArrowRight size={14} />
            </Link>
            <a
              href="https://www.tiktok.com/@cindymaks_collections"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-bg text-sm uppercase tracking-[0.2em] underline underline-offset-8 hover:text-brand-gold"
            >
              Follow on TikTok
            </a>
          </div>
        </div>
      </section>

      {/* PRODUCT GRID — mobile-first, swipeable feel */}
      <section className="bg-brand-bg text-brand-ink py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12">
            <div className="overline">As seen on TikTok</div>
            <h2 className="font-serif text-4xl md:text-5xl mt-3 tracking-tight">
              Shop the looks
            </h2>
            <p className="text-brand-muted mt-4 text-sm">Tap any piece — your 10% off is already on.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
            {products.map((p, idx) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                data-testid={`tiktok-product-${p.id}`}
                className="group animate-fade-up"
                style={{ animationDelay: `${idx * 80}ms` }}
              >
                <div className="aspect-[3/4] overflow-hidden bg-brand-bg2 relative">
                  <img src={p.image} alt={p.name} className="product-img w-full h-full object-cover" />
                  <div className="absolute top-2 left-2 bg-brand-accent text-white text-[10px] tracking-[0.15em] uppercase px-2 py-1 inline-flex items-center gap-1">
                    <Sparkles size={10} /> 10% off
                  </div>
                </div>
                <div className="mt-3">
                  <h3 className="font-serif text-base md:text-lg leading-tight">{p.name}</h3>
                  <div className="flex items-baseline gap-2 mt-1 text-sm">
                    <span className="text-brand-muted line-through">£{p.price.toFixed(2)}</span>
                    <span className="text-brand-accent font-medium">£{(p.price * 0.9).toFixed(2)}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why-buy bar */}
      <section className="bg-brand-bg2 py-10 px-6">
        <div className="max-w-[1100px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center text-brand-ink">
          <div>
            <div className="overline">Hand-tailored</div>
            <p className="text-xs text-brand-muted mt-1">Made in small batches</p>
          </div>
          <div>
            <div className="overline">UK shipping</div>
            <p className="text-xs text-brand-muted mt-1">2–5 working days</p>
          </div>
          <div>
            <div className="overline">Pay your way</div>
            <p className="text-xs text-brand-muted mt-1">Card · Klarna · Clearpay · PayPal</p>
          </div>
          <div>
            <div className="overline">Authentic Ankara</div>
            <p className="text-xs text-brand-muted mt-1">Heritage prints, every piece</p>
          </div>
        </div>
      </section>
    </div>
  );
}
